/**
 * The screen-pop.
 *
 * A call rings, and before the agent says hello they can see which line it came
 * in on, who is calling, and whether there is already a job open for them. One
 * click turns it into a case with all of that carried across.
 *
 * DESIGN RULES, each learned from how this goes wrong:
 *
 *  - The DID banner is the loudest thing on it. "EMERGENCY" and "GCC" want
 *    different voices answering, and that decision happens in about a second.
 *  - It is DISMISSIBLE and stays dismissed. A banner that reappears because the
 *    PBX sent another state change for the same call is one people learn to
 *    ignore, which defeats the point.
 *  - An existing open case for that caller is offered BEFORE "create new". The
 *    common repeat call is "where is the ambulance", and creating a second case
 *    for it is the mistake this should prevent rather than enable.
 *  - No patient name. The pop says "known patient"; the record opens with a
 *    reason attached and gets access-logged. Recognising a caller is not the
 *    same act as reading their chart.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, type CallScreenPop } from '../api/client.js';
import { CallerHistory } from './CallerHistory.js';

export interface RingingCall {
  callId: string;
  from: string;
  didLabel: string | null;
  didColour: string | null;
  isEmergency: boolean;
  topMatch: string | null;
  caseId: string | null;
}

const MATCH_LABEL: Record<string, string> = {
  client: 'Account',
  client_contact: 'Contact',
  patient: 'Patient',
  staff: 'Staff',
  recent_case: 'Open case',
};

export function IncomingCall({ call, onDismiss }: { call: RingingCall; onDismiss: () => void }) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // The event carries only ids. Detail comes from the API, where permissions are
  // re-checked and the read is on the audited path.
  const { data } = useQuery({
    queryKey: ['call', call.callId],
    queryFn: () => api.get<CallScreenPop>(`/calls/${call.callId}`),
  });

  const createCase = useMutation({
    mutationFn: () => api.post<{ id: string }>(`/calls/${call.callId}/case`, {}),
    onSuccess: (created) => {
      onDismiss();
      void navigate(`/cases/${created.id}`);
    },
    onError: (e: Error) => setError(e.message),
  });

  // Escape dismisses. A dispatcher with a phone at their ear is not reaching for
  // a mouse.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const colour = call.didColour ?? (call.isEmergency ? '#DC2626' : '#0284C7');
  const matches = data?.matches ?? [];
  const openCase = matches.find((m) => m.kind === 'recent_case');

  return (
    <div className="fixed right-4 top-4 z-50 w-[26rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">
      {/* Which line rang. The single most useful thing on the whole banner. */}
      <div
        className="flex items-center justify-between px-4 py-2"
        style={{ backgroundColor: colour }}
      >
        <span className="text-sm font-bold uppercase tracking-wide text-white">
          {call.didLabel ?? 'Incoming call'}
        </span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="rounded px-1.5 text-white/80 hover:bg-black/20 hover:text-white"
        >
          ×
        </button>
      </div>

      <div className="p-4">
        <p className="font-mono text-lg text-slate-100">{call.from}</p>

        {matches.length > 0 ? (
          <ul className="mt-2 space-y-1">
            {matches.slice(0, 4).map((m, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                <span className="rounded bg-slate-700 px-1.5 py-0.5 text-[10px] uppercase text-slate-300">
                  {MATCH_LABEL[m.kind] ?? m.kind}
                </span>
                <span className="text-slate-100">{m.label}</span>
                {m.detail && <span className="text-xs text-slate-400">{m.detail}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-slate-400">Number not recognised</p>
        )}

        {/* The whole story for this number: past cases, past calls, outcomes.
            A repeat caller is the common case, and the old system made them a
            stranger every time. */}
        <div className="mt-3 border-t border-slate-800 pt-3">
          <CallerHistory phone={call.from} onNavigate={onDismiss} />
        </div>

        {error && (
          <p className="mt-2 text-sm text-red-400" role="alert">
            {error}
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          {/* An open case for this caller is almost always what they are ringing
              about — "where is the ambulance" — so it is the loud option and
              "New case" becomes the quiet one. Creating a duplicate for a repeat
              caller is the mistake this exists to prevent. */}
          {openCase?.caseId && (
            <button
              type="button"
              onClick={() => {
                onDismiss();
                void navigate(`/cases/${openCase.caseId}`);
              }}
              className="flex-1 rounded bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-500"
            >
              Open {openCase.label} →
            </button>
          )}
          <button
            type="button"
            disabled={createCase.isPending}
            onClick={() => createCase.mutate()}
            className={
              'rounded px-3 py-1.5 text-sm font-medium disabled:opacity-50 ' +
              (openCase
                ? 'bg-slate-700 text-slate-100 hover:bg-slate-600'
                : 'bg-red-600 text-white hover:bg-red-500')
            }
          >
            {createCase.isPending ? 'Creating…' : 'New case'}
          </button>
        </div>
      </div>
    </div>
  );
}
